/**
 * TMC motion runtime. Copy to assets/tmc-motion.js.
 * Loaded as a module by every tmc-* section (identical module URLs run once).
 * Exposes window.TMC.motion and fires `tmc:motion-ready`.
 * See .claude/skills/tmc-section-builder/references/motion-runtime.md.
 */

/** @typedef {'off' | 'reduced' | 'lite' | 'full'} Tier */

const reduceQuery = matchMedia('(prefers-reduced-motion: reduce)');
const assets = JSON.parse(document.querySelector('script[data-tmc-assets]')?.textContent || '{}');
/** @type {Map<string, Promise<void>>} */
const scripts = new Map();
let configured = false;
/** @type {Tier[]} */
const order = ['off', 'reduced', 'lite', 'full'];

/**
 * Injects a classic script once.
 * @param {string | undefined} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
  if (!src) return Promise.reject(new Error('TMC motion: asset missing from tmc-motion-assets'));
  if (!scripts.has(src)) {
    scripts.set(
      src,
      new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`TMC motion: failed to load ${src}`));
        document.head.append(script);
      })
    );
  }
  return /** @type {Promise<void>} */ (scripts.get(src));
}

/** @returns {Tier} */
function deviceTier() {
  if (reduceQuery.matches) return 'reduced';
  const nav = /** @type {Navigator & { deviceMemory?: number, connection?: { saveData?: boolean, effectiveType?: string } }} */ (
    navigator
  );
  const connection = nav.connection;
  const lowEnd =
    (nav.deviceMemory ?? 8) < 4 ||
    (nav.hardwareConcurrency ?? 8) < 4 ||
    connection?.saveData === true ||
    /(^|-)(2g|3g)$/.test(connection?.effectiveType ?? '');
  return lowEnd ? 'lite' : 'full';
}

/**
 * The lower of the device tier and the merchant's per-section setting.
 * @param {string} [sectionSetting] - 'full' | 'subtle' | 'off'
 * @returns {Tier}
 */
function tier(sectionSetting = 'full') {
  /** @type {Tier} */
  let wanted = 'full';
  if (sectionSetting === 'off') wanted = 'off';
  if (sectionSetting === 'subtle') wanted = 'lite';
  return order[Math.min(order.indexOf(deviceTier()), order.indexOf(wanted))] ?? 'off';
}

/**
 * Loads GSAP (and optional plugins) once and registers the plugins.
 * @param {{ scrollTrigger?: boolean, splitText?: boolean }} [options]
 */
async function loadGsap({ scrollTrigger = false, splitText = false } = {}) {
  await loadScript(assets.gsap);
  const plugins = [];
  if (scrollTrigger) plugins.push(loadScript(assets.scrollTrigger));
  if (splitText) plugins.push(loadScript(assets.splitText));
  await Promise.all(plugins);

  const { gsap, ScrollTrigger, SplitText } = /** @type {any} */ (window);
  const registered = [];
  if (scrollTrigger && ScrollTrigger) registered.push(ScrollTrigger);
  if (splitText && SplitText) registered.push(SplitText);
  gsap.registerPlugin(...registered);
  if (ScrollTrigger && !configured) {
    ScrollTrigger.config({ ignoreMobileResize: true });
    configured = true;
  }
  return { gsap, ScrollTrigger, SplitText };
}

/** @type {ReturnType<typeof setTimeout> | undefined} */
let refreshTimer;
/** Debounced ScrollTrigger.refresh() after layout changes (editor reloads, late content). */
function refresh() {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => /** @type {any} */ (window).ScrollTrigger?.refresh(), 150);
}

/** @type {Promise<any> | null} */
let smoothScroll = null;
/**
 * Desktop-only Lenis smooth scrolling on GSAP's ticker. Resolves to the Lenis instance or null.
 * @param {{ prevent?: (node: Element) => boolean }} [options]
 */
function enableSmoothScroll(options = {}) {
  const designMode = /** @type {any} */ (window).Shopify?.designMode;
  const desktop = matchMedia('(pointer: fine) and (min-width: 990px)').matches;
  if (designMode || tier() !== 'full' || !desktop) return Promise.resolve(null);

  smoothScroll ??= (async () => {
    const [{ gsap, ScrollTrigger }] = await Promise.all([loadGsap({ scrollTrigger: true }), loadScript(assets.lenis)]);
    const lenis = new /** @type {any} */ (window).Lenis({ prevent: options.prevent });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((/** @type {number} */ time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
    document.documentElement.classList.add('tmc-smooth-scroll');
    return lenis;
  })();
  return smoothScroll;
}

/*
 * Scroll-linked parallax. [data-tmc-parallax="<speed>"] moves vertically by speed × the distance of its
 * parent from the viewport centre, so it sits in place when centred and drifts as it enters or leaves.
 * Uses the CSS `translate` property, which composes with any `transform` animation on the same element.
 * The parent is measured (never the moving element itself) so the offset can't feed back into itself.
 */
/** @type {Map<HTMLElement, number>} */
const parallaxSpeeds = new Map();
/** @type {Set<HTMLElement>} */
const parallaxVisible = new Set();
let parallaxTicking = false;
const parallaxObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      const element = /** @type {HTMLElement} */ (entry.target);
      if (entry.isIntersecting) parallaxVisible.add(element);
      else parallaxVisible.delete(element);
    }
    requestParallax();
  },
  { rootMargin: '25% 0px' }
);

function requestParallax() {
  if (parallaxTicking) return;
  parallaxTicking = true;
  requestAnimationFrame(updateParallax);
}

function updateParallax() {
  parallaxTicking = false;
  const centre = window.innerHeight / 2;
  for (const element of parallaxVisible) {
    if (!element.isConnected) {
      parallaxVisible.delete(element);
      parallaxSpeeds.delete(element);
      continue;
    }
    const reference = element.parentElement ?? element;
    const rect = reference.getBoundingClientRect();
    const distance = rect.top + rect.height / 2 - centre;
    element.style.translate = `0 ${(-distance * (parallaxSpeeds.get(element) ?? 0)).toFixed(1)}px`;
  }
}

/** @param {ParentNode} root */
function scanParallax(root) {
  for (const element of /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll('[data-tmc-parallax]'))) {
    if (parallaxSpeeds.has(element)) continue;
    parallaxSpeeds.set(element, Number(element.dataset.tmcParallax) || 0);
    parallaxObserver.observe(element);
  }
}

/*
 * Lights on. [data-tmc-lights] starts dark (data-dark) and flips to data-lit once a quarter of it is
 * visible (immediately, if it already is); CSS plays the flicker. Reduced motion and no-JS stay lit.
 * No "is it on screen yet" check: layout is still settling (fonts, images) when this runs.
 */
/** @param {ParentNode} root */
function scanLights(root) {
  for (const element of /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll('[data-tmc-lights]:not([data-tmc-lights-ready])'))) {
    element.dataset.tmcLightsReady = '';
    element.dataset.dark = '';
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        delete element.dataset.dark;
        element.dataset.lit = '';
      },
      { threshold: 0.45 }
    );
    observer.observe(element);
  }
}

/*
 * Reveal (Apple "StaggeredFadeIn"): inside [data-tmc-reveal], each [data-tmc-reveal-item] fades up in
 * turn the first time the group is a fifth on screen. Used for section headers only, never for every
 * element on the page. Hidden start states exist only once this has run (data-tmc-reveal-ready), so
 * no-JS and reduced motion see everything at once.
 */
/** @param {ParentNode} root */
function scanReveal(root) {
  for (const group of /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll('[data-tmc-reveal]:not([data-tmc-reveal-ready])'))) {
    const items = /** @type {NodeListOf<HTMLElement>} */ (group.querySelectorAll('[data-tmc-reveal-item]'));
    items.forEach((item, index) => item.style.setProperty('--tmc-reveal-i', String(index)));
    group.dataset.tmcRevealReady = '';
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        group.dataset.tmcRevealOn = '';
      },
      { threshold: 0.2 }
    );
    observer.observe(group);
  }
}

/** @param {ParentNode} root */
function scan(root) {
  const current = tier();
  if (current === 'off' || current === 'reduced') return;
  scanParallax(root);
  scanLights(root);
  scanReveal(root);
  requestParallax();
}

// Capture on document: on desktop (990px+) Horizon scrolls .page-wrapper, not the window, and scroll does not bubble.
document.addEventListener('scroll', requestParallax, { passive: true, capture: true });
window.addEventListener('resize', requestParallax, { passive: true });
scan(document);

document.addEventListener('shopify:section:load', (event) => {
  scan(/** @type {HTMLElement} */ (event.target));
  refresh();
});
document.addEventListener('shopify:section:unload', refresh);

const motion = { tier, loadGsap, loadScript, refresh, enableSmoothScroll, assets, scan };
/** @type {any} */ (window).TMC = Object.assign(/** @type {any} */ (window).TMC ?? {}, { motion });
window.dispatchEvent(new Event('tmc:motion-ready'));
